import { FormControlLabel } from "@commonlibs";
import clsx from "clsx";
import UploadInput from "ui/common/components/input/UploadInput/UploadInput";
import { UploadInputChildrenProps, UploadedFile, UploadInputProps } from "ui/common/components/input/UploadInput/UploadInput.types";
import renderUploadInputChildren from "ui/common/components/input/UploadInput/util/renderUploadInputChildren";
import { FileUploadRoot } from "./FileUpload.styles";
import { FileUploadProps } from "./FileUpload.types";
import UploadInputDisplay from "./UploadInputDisplay/UploadInputDisplay";

function FileUpload<Multiple extends boolean = false>({
    //UploadProps
    fileTypes,
    readonly,
    disabled,
    multiple,
    children,
    onError,
    onFileUploaded,
    onUploadEnd,
    onUploadStart,
    upload,
    onSuccess,
    isError,
    isLoading,
    isSuccess,
    //UploadInputDisplayProps
    placeholder,
    keepPlaceholder,
    drag,
    showLoading = true,
    size = "md",
    fullWidth = false,
    //FormControlProps
    Label = FormControlLabel,
    LabelProps = {},
    defaultValue,
    showValid = false,
    hideError = false,
    ...rest
}: FileUploadProps<Multiple>): JSX.Element {
    const uploadProps: Partial<UploadInputProps> = {
        fileTypes,
        readonly,
        disabled,
        multiple,
        children,
        onError,
        onFileUploaded,
        onUploadStart,
        onSuccess,
        upload,
        isLoading,
    };

    return (
        <FileUploadRoot
            Label={[Label, LabelProps]}
            defaultValue={defaultValue}
            readonly={readonly}
            disabled={disabled}
            showValid={showValid}
            hideError={hideError}
            className={clsx("file-upload", size, { "full-width": fullWidth })}
            {...rest}
        >
            {(value, setValue) => {
                function handleFileChange(fileUri: UploadedFile | undefined) {
                    setValue(fileUri as (Multiple extends true ? string[] : string) | undefined);
                    onUploadEnd?.(fileUri);
                }
                return (
                    <UploadInput className="file-upload-input" onUploadEnd={handleFileChange} {...uploadProps}>
                        {(params) => {
                            const finalParams: UploadInputChildrenProps = {
                                ...params,
                                fileUri: value,
                                isError: isError !== undefined ? isError : params.isError,
                                isSuccess: isSuccess !== undefined ? isSuccess : params.isSuccess,
                            };
                            return (
                                <UploadInputDisplay
                                    dragElement={drag}
                                    showError={!hideError}
                                    showSuccess={showValid}
                                    showLoading={showLoading}
                                    size={size}
                                    fullWidth={fullWidth}
                                    {...finalParams}
                                >
                                    {value !== undefined && !keepPlaceholder
                                        ? renderUploadInputChildren(children, finalParams)
                                        : placeholder}
                                </UploadInputDisplay>
                            );
                        }}
                    </UploadInput>
                );
            }}
        </FileUploadRoot>
    );
}

export default FileUpload;
